import React from 'react';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';
import { useWorkspacesPreambleBoardInviteButtonWrapperQuery } from '@trello/graphql/generated';
import { WorkspacesPreambleBoardInviteButton } from './WorkspacesPreambleBoardInviteButton';
import { forTemplate } from 'app/common/lib/util/i18n';

const format = forTemplate('board_header');

interface WorkspacesPreambleBoardInviteButtonWrapperProps {
  boardId: string;
  onSuccess: () => void;
}

export const WorkspacesPreambleBoardInviteButtonWrapper: React.FunctionComponent<WorkspacesPreambleBoardInviteButtonWrapperProps> = ({
  boardId,
  onSuccess,
}) => {
  const { data } = useWorkspacesPreambleBoardInviteButtonWrapperQuery({
    variables: { boardId },
  });

  /**
   * For the invite button to open the prompt, the board and user must meet the following:
   * - The board does not belong to a team
   * - The board has no members other than the admin
   * - The user is the admin
   * - The user is not a member of an enterprise team
   * If the board and user do not meet the requirements for the WorkspacesPreamble feature, show the classic Invite button
   */
  const meId = data?.member?.id;
  const meMembership = data?.board?.memberships?.find(
    (membership) => membership.idMember === meId,
  );
  const isBoardAdmin = meMembership?.memberType === 'admin';
  const isEnterpriseMember = !!data?.member?.enterprises?.length;
  const isTeamless = !data?.board?.idOrganization;
  const hasNoCollaborators = data?.board?.members?.length === 1;
  const boardAndMemberMeetRequirements = !!(
    isBoardAdmin &&
    isTeamless &&
    hasNoCollaborators &&
    !isEnterpriseMember
  );

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.WorkspacesPreamble,
      }}
    >
      {boardAndMemberMeetRequirements ? (
        <WorkspacesPreambleBoardInviteButton
          boardId={boardId}
          onSuccess={onSuccess}
        />
      ) : (
        <a
          className="board-header-btn board-header-btn-invite board-header-btn-without-icon js-open-manage-board-members"
          title={format('invite-to-board')}
        >
          <span className="board-header-btn-text">{format('invite')}</span>
        </a>
      )}
    </ErrorBoundary>
  );
};
